import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/useAuth';
import { useTheme } from '../contexts/useTheme';
import {
    LayoutDashboard,
    Clock,
    Calendar,
    FolderKanban,
    CheckSquare,
    BarChart3,
    Settings,
    Shield,
    Users,
    UserCheck,
    LogOut,
    Menu,
    X,
    Sun,
    Moon,
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '../lib/utils';
import { NotificationDropdown } from './NotificationDropdown';
import { APP_VERSION } from '../version';
import { config, isProduction } from '../config';

interface LayoutProps {
    children: ReactNode;
}

const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Saisie', href: '/time-entry', icon: Clock },
    { name: 'Mois', href: '/month', icon: Calendar },
    { name: 'Projets', href: '/projects', icon: FolderKanban },
    { name: 'Rapports', href: '/reports', icon: BarChart3 },
    { name: 'Portail Client', href: '/client-portal', icon: UserCheck, hideForAdmin: true },
];

const managerNavigation = [
    { name: 'Validations', href: '/approvals', icon: CheckSquare },
];

const adminNavigation = [
    { name: 'Collab', href: '/collab', icon: Users },
    { name: 'Admin', href: '/admin', icon: Shield },
];

export function Layout({ children }: LayoutProps) {
    const { user, logout, isManager, isAdmin } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const allNavigation = [
        ...navigation.filter(item => !(item.hideForAdmin && isAdmin)),
        ...(isManager ? managerNavigation : []),
        ...(isAdmin ? adminNavigation : []),
    ];

    // Get initials from name or email
    const getInitials = (name?: string, email?: string) => {
        if (name) {
            return name
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2);
        }
        if (email) {
            return email.split('@')[0].charAt(0).toUpperCase();
        }
        return '?';
    };

    // Get display name
    const getDisplayName = (name?: string, email?: string) => {
        if (name) return name;
        if (email) {
            const namePart = email.split('@')[0];
            return namePart.charAt(0).toUpperCase() + namePart.slice(1);
        }
        return '...';
    };

    return (
        <div className="min-h-screen bg-[var(--color-bg)]">
            {/* Header */}
            <header className="bg-[#020873] border-b border-[rgba(255,255,255,0.1)] sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* Logo */}
                        <Link to="/" className="flex items-center gap-3">
                            <img
                                src="/logo-clippit.png"
                                alt="Clippit"
                                className="h-10 w-auto logo-wiggle"
                            />
                            <span className="font-semibold text-lg text-white">CRA</span>
                            {/* Repère visuel hors production : évite de saisir un CRA sur le mauvais environnement. */}
                            {!isProduction && (
                                <span className="px-2 py-0.5 rounded-full bg-[#16ff2d] text-[#020873] text-[10px] font-bold uppercase tracking-wider">
                                    {config.environment}
                                </span>
                            )}
                        </Link>

                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex items-center gap-1">
                            {allNavigation.map((item) => {
                                const isActive = location.pathname === item.href ||
                                    (item.href !== '/' && location.pathname.startsWith(item.href));
                                return (
                                    <Link
                                        key={item.name}
                                        to={item.href}
                                        className={cn(
                                            'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                                            isActive
                                                ? 'bg-[rgba(255,255,255,0.15)] text-[#16ff2d]'
                                                : 'text-[rgba(255,255,255,0.8)] hover:text-[#16ff2d] hover:bg-[rgba(255,255,255,0.05)]'
                                        )}
                                    >
                                        {item.name}
                                    </Link>
                                );
                            })}
                        </nav>

                        {/* Right side */}
                        <div className="flex items-center gap-3">
                            {/* Notifications */}
                            <NotificationDropdown />

                            {/* User */}
                            <div className="hidden sm:flex items-center gap-2">
                                <div className="w-8 h-8 bg-[rgba(255,255,255,0.15)] rounded-full flex items-center justify-center">
                                    <span className="text-white font-medium text-sm">
                                        {getInitials(user?.displayName, user?.email)}
                                    </span>
                                </div>
                                <span className="hidden lg:block text-sm font-medium text-white max-w-[120px] truncate">
                                    {getDisplayName(user?.displayName, user?.email)}
                                </span>
                            </div>

                            {/* Settings & Logout */}
                            <div className="hidden sm:flex items-center gap-1">
                                <button
                                    onClick={toggleTheme}
                                    className="p-2 rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                                    title={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
                                >
                                    {theme === 'dark' ? (
                                        <Sun className="w-4 h-4 text-[rgba(255,255,255,0.6)]" />
                                    ) : (
                                        <Moon className="w-4 h-4 text-[rgba(255,255,255,0.6)]" />
                                    )}
                                </button>
                                <Link
                                    to="/settings"
                                    className="p-2 rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                                    title="Paramètres"
                                >
                                    <Settings className="w-4 h-4 text-[rgba(255,255,255,0.6)]" />
                                </Link>
                                <button
                                    onClick={logout}
                                    className="p-2 rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                                    title="Se déconnecter"
                                >
                                    <LogOut className="w-4 h-4 text-[rgba(255,255,255,0.6)]" />
                                </button>
                            </div>

                            {/* Mobile menu button */}
                            <button
                                className="md:hidden p-2 text-white hover:bg-[rgba(255,255,255,0.1)] rounded-lg"
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            >
                                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden bg-[#020873] border-t border-[rgba(255,255,255,0.1)]">
                        <div className="px-4 py-3 space-y-1">
                            {allNavigation.map((item) => {
                                const isActive = location.pathname === item.href ||
                                    (item.href !== '/' && location.pathname.startsWith(item.href));
                                const Icon = item.icon;
                                return (
                                    <Link
                                        key={item.name}
                                        to={item.href}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className={cn(
                                            'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium',
                                            isActive
                                                ? 'bg-[rgba(255,255,255,0.15)] text-[#16ff2d]'
                                                : 'text-[rgba(255,255,255,0.8)]'
                                        )}
                                    >
                                        <Icon className="w-5 h-5" />
                                        {item.name}
                                    </Link>
                                );
                            })}
                            <hr className="border-[rgba(255,255,255,0.1)] my-2" />
                            <button
                                onClick={toggleTheme}
                                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-[rgba(255,255,255,0.8)] w-full"
                            >
                                {theme === 'dark' ? (
                                    <Sun className="w-5 h-5" />
                                ) : (
                                    <Moon className="w-5 h-5" />
                                )}
                                {theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
                            </button>
                            <Link
                                to="/settings"
                                onClick={() => setMobileMenuOpen(false)}
                                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-[rgba(255,255,255,0.8)]"
                            >
                                <Settings className="w-5 h-5" />
                                Paramètres
                            </Link>
                            <button
                                onClick={() => {
                                    setMobileMenuOpen(false);
                                    logout();
                                }}
                                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-[rgba(255,255,255,0.8)] w-full"
                            >
                                <LogOut className="w-5 h-5" />
                                Se déconnecter
                            </button>
                        </div>
                    </div>
                )}
            </header>

            {/* Mobile navigation bar */}
            <nav className="md:hidden bg-[var(--color-bg-card)] border-b border-[var(--color-border)] overflow-x-auto">
                <div className="flex px-2 gap-1">
                    {allNavigation.slice(0, 5).map((item) => {
                        const isActive = location.pathname === item.href ||
                            (item.href !== '/' && location.pathname.startsWith(item.href));
                        const Icon = item.icon;
                        return (
                            <Link
                                key={item.name}
                                to={item.href}
                                className={cn(
                                    'flex flex-col items-center gap-1 px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors min-w-[60px]',
                                    isActive
                                        ? 'text-[var(--color-primary)] border-b-2 border-[var(--color-primary)]'
                                        : 'text-[var(--color-text-secondary)]'
                                )}
                            >
                                <Icon className="w-4 h-4" />
                                {item.name}
                            </Link>
                        );
                    })}
                </div>
            </nav>

            {/* Main content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {children}
            </main>

            {/* Footer */}
            <footer className="border-t border-[var(--color-border)] mt-auto">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
                    <p className="text-center text-xs text-[var(--color-text-muted)]">
                        Clippit CRA · v{APP_VERSION}
                        {!isProduction && ` · ${config.environment}`}
                    </p>
                </div>
            </footer>
        </div>
    );
}
